import {
  DocumentTextIcon,
  MapPinIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { memo } from 'react';

import type { ReadPoliceReport } from '@/services/api/generated/policeReportsService';
import { enhancedPoliceReportsApi } from '@/services/api/policeReportsService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';
import { PoliceDepartmentLink } from './PoliceDepartmentLink';

/**
 * Police report tooltip content component
 * Shows attestation status, department, report identifier, and location.
 */
export const PoliceReportTooltipContent = memo<
  EntityTooltipContentProps<ReadPoliceReport>
>(({ fetchState }) => {
  if (fetchState.status === 'pending') {
    return <LoadingState rows={2} />;
  }

  if (fetchState.status === 'error') {
    return <ErrorState error={fetchState.error} entityType="police report" />;
  }

  if (fetchState.status === 'idle' || !fetchState.data) {
    return <EmptyState entityType="police report" />;
  }

  const report = fetchState.data;
  const {
    attestation_status,
    report_identifier,
    reported_location,
    police_department,
  } = report;

  const title = report_identifier
    ? `Report #${report_identifier}`
    : 'Police Report';

  return (
    <div className="divide-y divide-gray-100">
      <TooltipHeader
        icon={ShieldCheckIcon}
        title={title}
        badges={
          attestation_status
            ? createStatusBadge({ value: attestation_status.replace(/_/g, ' ') })
            : undefined
        }
      />

      <TooltipSection withBorder>
        {police_department && (
          <InfoRow
            icon={DocumentTextIcon}
            label="Department"
            value={
              <PoliceDepartmentLink
                entityId={police_department.id}
                side="left"
                className="text-xs"
              >
                {police_department.name || 'Department'}
              </PoliceDepartmentLink>
            }
            disableCopy
          />
        )}
        {reported_location && (
          <InfoRow
            icon={MapPinIcon}
            label="Location"
            value={reported_location}
            wrapValue
          />
        )}
      </TooltipSection>
    </div>
  );
});

PoliceReportTooltipContent.displayName = 'PoliceReportTooltipContent';

/**
 * Hoverable police report link with tooltip preview
 *
 * Shows report identifier, attestation status, department, and location on hover.
 *
 * @example
 * ```tsx
 * <PoliceReportLink entityId={reportId} matterId={matterId}>
 *   Police Report
 * </PoliceReportLink>
 * ```
 */
export const PoliceReportLink = createEntityLink<
  ReadPoliceReport,
  { id: string; matterId: string },
  { matterId: string }
>({
  tooltipContent: PoliceReportTooltipContent,
  getDetailUrl: null,
  useQuery:
    enhancedPoliceReportsApi.endpoints.policeReportsRetrieve.useQuery,
  getQueryArg: (id, { matterId }) => ({ id, matterId }),
  displayName: 'PoliceReportLink',
});
